"use client";

import React, { useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { getCategoriesWithProductCount } from "@/modules/product/product.service";
import Image from "next/image";

export const Footer = () => {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const { CategoryWithCountList } = useAppSelector((state) => state.category);
  const { isCustomerAuthenticated } = useAppSelector((state) => state.auth);

  const isShopRoute =
    pathname?.includes("/shop") ||
    pathname?.includes("/cost-to-cost") ||
    pathname?.includes("/other-products");

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // On shop, cost-to-cost, and other-products pages, ShopSidebar handles fetching categories with active filters
    if (isShopRoute) return;

    if (!hasFetchedRef.current && CategoryWithCountList.length === 0) {
      hasFetchedRef.current = true;
      dispatch(
        getCategoriesWithProductCount({
          IsOnline: false,
          IsCostToCost: false,
          IsOthersProduct: false,
        })
      );
    }
  }, [dispatch, isShopRoute, CategoryWithCountList.length]);

  return (
    <footer
      className="text-off-white border-t border-white/10 px-4 md:px-14 pt-14 pb-7"
      style={{
        background:
          "linear-gradient(145deg, #0d0202 0%, #2a0a0a 35%, #5c2020 65%, #7a2a2a 100%)",
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-11 mb-11">
        <div className="space-y-4">
          <Link
            href="/"
            className="inline-block bg-white p-2.5 rounded-sm shadow-xl"
          >
            <Image
              src="/images/logo.jpeg"
              alt="NakMan Jewellery"
              width={180}
              height={70}
              className="h-[60px] w-auto object-contain"
            />
          </Link>
          <span className="text-[9px] text-white/50 tracking-[0.2em] uppercase block font-medium">
            Premium imitation jewellery wholesale
          </span>
          <p className="text-sm text-white/80 leading-[1.75] font-light max-w-[280px]">
            Premium imitation jewellery wholesale.
            <br />
            Trusted by retailers and online sellers across India.
          </p>
        </div>

        <div>
          <h4 className="text-[10px] font-bold tracking-[0.22em] uppercase text-white mb-5">
            Shop
          </h4>
          <ul className="flex flex-col gap-3 list-none">
            {CategoryWithCountList.slice(0, 6).map((cat) => (
              <li key={cat.CategoryID}>
                <Link
                  href={`/shop?category=${cat.CategoryID}`}
                  className="text-md text-white/70 font-light hover:text-white transition-colors flex justify-between items-center group"
                >
                  <span>{cat.CategoryName}</span>
                  <span className="text-[10px] opacity-40 group-hover:opacity-100 transition-opacity">
                    ({cat.ProductCount})
                  </span>
                </Link>
              </li>
            ))}
            <li>
              <Link
                href="/shop"
                className="text-md font-medium text-white hover:text-amber-200 transition-colors mt-1 block uppercase tracking-widest text-[10px]"
              >
                View All Collection &rarr;
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-[10px] font-bold tracking-[0.22em] uppercase text-white mb-5">
            Company
          </h4>
          <ul className="flex flex-col gap-3 list-none">
            <li>
              <Link
                href="/about"
                className="text-md text-white/70 font-light hover:text-white transition-colors"
              >
                About Us
              </Link>
            </li>
            <li>
              <Link
                href="/blog"
                className="text-md text-white/70 font-light hover:text-white transition-colors"
              >
                Blog / News
              </Link>
            </li>
            <li>
              <Link
                href="/wholesale"
                className="text-md text-white/70 font-light hover:text-white transition-colors"
              >
                Wholesale Inquiry
              </Link>
            </li>
            <li>
              <a
                href="https://www.indiamart.com/malabarenterprise/"
                target="_blank"
                className="text-md text-white/70 font-light hover:text-white transition-colors"
              >
                IndiaMart Profile
              </a>
            </li>
            <li>
              <Link
                href="/contact"
                className="text-md text-white/70 font-light hover:text-white transition-colors"
              >
                Contact Us
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-[10px] font-bold tracking-[0.22em] uppercase text-white mb-5">
            Support
          </h4>
          <ul className="flex flex-col gap-3 list-none">
            <li>
              <Link
                href="/shipping"
                className="text-md text-white/70 font-light hover:text-white transition-colors"
              >
                Shipping Info
              </Link>
            </li>
            <li>
              <Link
                href="/returns"
                className="text-md text-white/70 font-light hover:text-white transition-colors"
              >
                Returns Policy
              </Link>
            </li>
            <li>
              <Link
                href="/privacy"
                className="text-md text-white/70 font-light hover:text-white transition-colors"
              >
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link
                href="/terms"
                className="text-md text-white/70 font-light hover:text-white transition-colors"
              >
                Terms & Conditions
              </Link>
            </li>
            {isCustomerAuthenticated && (
              <li>
                <Link
                  href="/my-account"
                  className="text-md text-white/70 font-light hover:text-white transition-colors"
                >
                  My Account
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10 pt-5 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
        <p className="text-md text-white/50 font-light">
          &copy; {new Date().getFullYear()} NakMan Jewellery &middot; Malabar
          Enterprise &middot; Ahmedabad, Gujarat, India &middot; All rights
          reserved
        </p>
        <div className="border border-white/20 text-white text-[10px] font-bold tracking-[0.12em] px-4 py-1.5 uppercase shrink-0">
          Min. Order ₹3,000
        </div>
      </div>
    </footer>
  );
};
